import { Component, lazy } from 'react'
import { Helmet } from 'react-helmet'

import $ from 'jquery'
import { connect } from 'react-redux'
import { Route, Switch } from 'react-router-dom'
import track from 'react-tracking'
import { bindActionCreators } from 'redux'
import { initializeFaro as coreInit } from '@grafana/faro-react'

const ActivityContainerNewNav = lazy(() => import('./NavV2/ActivityContainer'))
const BookIndex = lazy(() => import('./BookIndexContainer/BookIndexContainer'))
const BookIndexContainer = lazy(() => import('./NavV2/BookIndexContainer'))
const ChatIA = lazy(() => import('./NavV2/ChatIA'))
const DocumentsContainer = lazy(() => import('./DocumentsContainer'))
const ExternalContainer = lazy(() => import('./External/ExternalContainer'))
const FlipbookContainer = lazy(() => import('./FlipbookContainer'))
const HotlinkListContainer = lazy(() => import('./HotlinkListContainer'))
const HtmlBookContainer = lazy(() => import('./NavV2/HtmlBookContainer'))
const LessonsListContainer = lazy(() => import('./LessonsListContainer'))
const LibraryListContainer = lazy(() => import('./LibraryListContainer'))
const LinksListContainer = lazy(() => import('./LinksListContainer'))
const MauthorAttemptContainer = lazy(() => import('./MauthorAttempt/MauthorAttemptContainer'))
const MauthorContainer = lazy(() => import('./MauthorContainer'))
const MauthorListContainer = lazy(() => import('./MauthorListContainer'))
const MultimediaContainer = lazy(() => import('./MultimediaContainer'))
const MultimediaVideoContainer = lazy(() => import('./MultimediaVideoContainer'))
const PdfContainer = lazy(() => import('./PdfContainer'))
const Redirect = lazy(() => import('./Redirect'))
const SimpleActivityContainer = lazy(() => import('./SimpleActivityContainer'))
const UnitContainer = lazy(() => import('./UnitContainer'))
const UnitCoverContainer = lazy(() => import('./Unit/Cover/UnitCoverContainer'))
const VideoContainer = lazy(() => import('./VideoContainer'))
const index = lazy(() => import('../assets/styles/scss/index.module.scss'))

import { ErrorBoundary } from '../components/Error'
import i18n from '../i18n'
import checkRequest from '../utils/CheckRequests/checkRequests'
import LoaderFullScreen from 'components/LoaderFullScreen/LoaderFullScreen'
import { userBook } from 'actions/BookActions'
import { template } from 'actions/TemplateActions'
import { BookCall } from 'calls'
import TrackingCall from 'calls/TrackingCall'
import { ModalProvider } from 'contexts/ModalContext/ModalContext'
import { GlobalModalProvider } from 'contexts/GlobalModalContext'
import {
  BOOK_TEMPLATES,
  NAVIGATION_TYPES,
  TRACKING_ACTIONS_ACCESS_EVENTS,
  TRACKING_ELEMENT_TYPE,
  TRACKING_EVENT_TYPE,
  USER_BOOK_STATUS,
  USER_ROLE_TYPES,
} from 'utils/constants/constants'
import grafanaUtils from 'utils/grafana/grafanaUtils'
import GoogleTagManager from 'utils/googleTagManager'
import DefaultError from 'components/Error/DefaultError/DefaultError'
import { CustomFaroErrorBoundary } from 'components/Error/CustomFaroErrorBoundary'
import { UnitViewFormatProvider } from 'contexts/UnitViewFormatProvider'
import RebrandingProvider from 'components/NavV2/shared/RebrandingProvider'
import { PaperViewProvider } from 'contexts/PaperViewProvider'

class App extends Component {
  bookId = false

  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      lang: 'es-ES',
    }
    this.initGlobalVars()

    const {
      match: { url },
    } = this.props
    const [, bookId] = /^\/([0-9]*)/.exec(url)
    this.bookId = bookId
  }

  initGlobalVars = () => {
    window.$ = $
    let vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
    window.addEventListener('resize', () => {
      vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    })
  }

  @track((props) => {
    if (!props.book?.book) return

    const {
      book: {
        book,
        user: { id: userId },
        userBook: { id: userBookId },
      },
    } = props
    const bookId = book?.bookId

    return {
      action: 'initTracking',
      page: 'AppContainer',
      params: {
        bookId,
        userBookId,
        userId,
      },
    }
  })
  initTracking = () => {}

  async initializeFaro() {
    try {
      const initialOptions = grafanaUtils.getInitialOptions()
      coreInit(initialOptions)
      grafanaUtils.setGrafanaUuidDevice()
    } catch (error) {
      console.error('initializeFaro', error)
    }
  }

  async componentDidMount() {
    const { setCurrentUserBook, setTemplate } = this.props
    const isGrafanaFaroEnabled = window.checkFeatureFlag('ff-grafana-faro')

    if (this.bookId) {
      try {
        const book = await BookCall.getUserBook(this.bookId)

        const isNav2023 = book.book?.navigationType === NAVIGATION_TYPES.nav2023
        const isTeacher = book.user?.role === USER_ROLE_TYPES.teacher
        if (isNav2023 && isTeacher) {
          await BookCall.getUserStatics()
        }
        if (isNav2023 && isGrafanaFaroEnabled) {
          await this.initializeFaro()
        }

        if (isNav2023 && isGrafanaFaroEnabled) {
          grafanaUtils.setGrafanaUser(book)
        }
        this.setState({ lang: book?.book?.locale ?? 'es-ES' })
        setCurrentUserBook(book)
        this.initTracking()
        localStorage.setItem('userLanguage', book?.user?.language ?? 'es')

        GoogleTagManager.initializeFromUserBook(book)

        const currentTemplate = book.book?.navigationParameters?.template ?? BOOK_TEMPLATES.teens
        setTemplate(currentTemplate)
      } catch (originalError) {
        this.setState({
          hasError: true,
        })

        if (isGrafanaFaroEnabled) {
          const errorStatus = originalError?.response?.status ?? originalError?.status
          const errorCode = originalError?.data?.code ?? originalError?.code

          const error = new Error(
            `Error fetching user book with status ${errorStatus}, code: ${errorCode}`
          )

          await this.initializeFaro()

          grafanaUtils.pushError(error, {
            context: {
              bookId: this.bookId,
              bookNavigation: originalError?.data?.book?.navigationType,
              errorResponse: JSON.stringify(originalError?.data),
              errorStatus: JSON.stringify(errorStatus),
              userBookId: originalError?.data?.userBook?.id,
              userId: originalError?.data?.user?.id,
            },
            type: 'network',
          })
        }
      }
    }
  }

  renderError() {
    return (
      <>
        <Helmet>
          <html lang={this.state.lang} />
        </Helmet>
        <DefaultError
          actionText={i18n.t('Por favor, inténtalo más tarde.')}
          description={i18n.t(
            'Algo salió mal... Estamos trabajando en ello para que vuelva a funcionar.'
          )}
          title={i18n.t('¡Oops!')}
        />
      </>
    )
  }

  getNav2023Layout() {
    return (
      <CustomFaroErrorBoundary>
        <>
          <Helmet>
            <html lang={this.state.lang} />
          </Helmet>
          <UnitViewFormatProvider>
            <PaperViewProvider>
              <GlobalModalProvider>
                <ModalProvider>
                  <ChatIA />
                  <Switch>
                    <Route
                      exact
                      path='/:bookId/htmlBook/:unitId/:sectionId?'
                      render={(props) => <HtmlBookContainer {...props} />}
                    />

                    <Route
                      exact
                      path='/:bookId/htmlBook/:unitId/:sectionId?/:activityId'
                      render={(props) => <ActivityContainerNewNav {...props} />}
                    />
                    <Route
                      path='/:bookId'
                      render={(props) => {
                        return <BookIndexContainer {...props} />
                      }}
                    />
                    <Route exact path='/' render={(props) => <Redirect {...props} />} />
                  </Switch>
                </ModalProvider>
              </GlobalModalProvider>
            </PaperViewProvider>
          </UnitViewFormatProvider>
        </>
      </CustomFaroErrorBoundary>
    )
  }

  getExternalLayout() {
    return (
      <CustomFaroErrorBoundary>
        <>
          <Helmet>
            <html lang={this.state.lang} />
          </Helmet>
          <Switch>
            <Route
              path='/:bookId'
              render={(props) => {
                return <ExternalContainer {...props} />
              }}
            />
            <Route exact path='/' render={(props) => <Redirect {...props} />} />
          </Switch>
        </>
      </CustomFaroErrorBoundary>
    )
  }

  getZoomLayout(book) {
    const bookLang = book.userBook?.book?.language || i18n.language
    const bookPhases =
      book.book?.navigationParameters?.pedagogicalPhases ||
      book.book?.navigationParameters?.learningPhases

    const layout = book.book?.type === 'FLIPBOOK_BASIC' ? 3 : 1

    return (
      <>
        <Helmet>
          <html lang={this.state.lang} />
        </Helmet>
        <GlobalModalProvider>
          <ModalProvider>
            <ChatIA />
            <div className={`${index[`Layout${layout}`]} Layout${layout}`}>
              <Switch>
                <Route
                  exact
                  path='/:bookId/:unitId/media-list/:pageNumber/:hotlinkType'
                  render={(props) => <HotlinkListContainer {...props} />}
                />
                <Route
                  exact
                  path='/:bookId/:unitId/:sectionId/flipbook/:pageNumber'
                  render={(props) => <FlipbookContainer {...props} />}
                />
                <Route
                  exact
                  path='/section/:sectionId/mauthor/:mauthorId'
                  render={(props) => (
                    <ErrorBoundary bookLang={bookLang}>
                      <MauthorContainer {...props} />
                    </ErrorBoundary>
                  )}
                />
                <Route
                  exact
                  path='/section/:sectionId/mauthors/:mauthorId'
                  render={(props) => (
                    <ErrorBoundary bookLang={bookLang}>
                      <MauthorContainer {...props} />
                    </ErrorBoundary>
                  )}
                />

                <Route
                  exact
                  path='/section/:sectionId/simpleactivity/:simpleActivityId'
                  render={(props) => (
                    <ErrorBoundary bookLang={bookLang}>
                      <SimpleActivityContainer {...props} />
                    </ErrorBoundary>
                  )}
                />

                <Route
                  exact
                  path='/section/:sectionId/pdf/:pdfId'
                  render={(props) => <PdfContainer {...props} />}
                />
                <Route
                  exact
                  path='/section/:sectionId/video/:videoId'
                  render={(props) => <VideoContainer {...props} />}
                />
                <Route
                  exact
                  path='/:bookId/:unitId/:sectionId/activities'
                  render={(props) => <MauthorListContainer {...props} />}
                />
                <Route
                  exact
                  path='/:bookId/:unitId/:sectionId/:pageNumber/:activities'
                  render={(props) => <LessonsListContainer {...props} />}
                />
                <Route
                  exact
                  path='/:bookId/:unitId/:sectionId/reading'
                  render={(props) => <LibraryListContainer {...props} />}
                />
                <Route
                  exact
                  path='/:bookId/:unitId/:sectionId/links'
                  render={(props) => <LinksListContainer {...props} />}
                />
                <Route
                  exact
                  path='/:bookId/:unitId/:sectionId/multimedia'
                  render={(props) => <MultimediaContainer {...props} />}
                />
                <Route
                  exact
                  path='/:bookId/:unitId/:sectionId/documents'
                  render={(props) => <DocumentsContainer {...props} />}
                />
                <Route
                  exact
                  path='/:bookId/:unitId/:sectionId/multimedia/video/:videoId'
                  render={(props) => <MultimediaVideoContainer {...props} />}
                />

                {bookPhases && (
                  <Route
                    path='/:bookId/:unitId'
                    render={(props) => <UnitCoverContainer {...props} />}
                  />
                )}
                {!bookPhases && (
                  <Route path='/:bookId/:unitId' render={(props) => <UnitContainer {...props} />} />
                )}
                <Route
                  path='/:bookId'
                  render={(props) => {
                    return <BookIndex {...props} />
                  }}
                />
                <Route exact path='/' render={(props) => <Redirect {...props} />} />
              </Switch>
            </div>
          </ModalProvider>
        </GlobalModalProvider>
      </>
    )
  }

  getResources = () => {
    import('../assets/custom_libraries/jquery/jquery-3.3.1')
    import('../assets/custom_libraries/vvdAnimation/vvdAnimation')
    import('../assets/custom_libraries/jquery-ui-1.12.1/jquery-ui')
    import('../assets/custom_libraries/vvdAnimation/vvdAnimationExpan')
    import('../assets/custom_libraries/vvdplayer/vvdPlayer')
    import('../assets/custom_libraries/vvdplayer/vvdPlayerReading')
    import('../assets/custom_libraries/fancybox/jquery.fancybox.pack')
    import('../assets/custom_libraries/nivo-slider/jquery.nivo.slider.min')
    import('../assets/custom_libraries/MathJax')
    import('../assets/custom_libraries/sas_dialog_2/sas_dialog_2')
    import('../assets/custom_libraries/farm-tabs/source/farm-tabs')
    import('../assets/custom_libraries/qs/qs-check')
    import('../assets/custom_libraries/qs/qs-create')
    import('../assets/custom_libraries/qs/qs-reset-hard')
    import('../assets/custom_libraries/qs/qs-resolve')
    // External css
    import('../assets/custom_libraries/fancybox/jquery.fancybox.css')
    import('../assets/custom_libraries/farm-tabs/source/farm-tabs.global.css')
    import('../assets/custom_libraries/vvdAnimation/vvdAnimation.global.css')
    import('../assets/custom_libraries/vvdAnimation/vvdAnimationExpan.global.css')
    import('../assets/custom_libraries/vvdplayer/vvdPlayer.global.css')
    import('../assets/custom_libraries/vvdplayer/vvdPlayerReading.global.css')
    import('../assets/custom_libraries/sas_dialog_2/sas_dialog_2.global.css')
    import('../assets/custom_libraries/nivo-slider/nivo.global.css')
    import('../assets/custom_libraries/nivo-slider/nivo-slider.global.css')
  }

  getResourcesNav2023 = () => {
    import('../assets/styles/scss/indexNav2023.module.scss')
  }

  render() {
    const { book } = this.props
    const { hasError } = this.state

    return <RebrandingProvider>{this.renderContent(book, hasError)}</RebrandingProvider>
  }

  renderContent(book, hasError) {
    if (hasError) {
      return this.renderError()
    }

    let layout = 1

    if (book || this.bookId) {
      if (!book?.book) {
        return <LoaderFullScreen />
      }

      const isNav2023 = book.book?.navigationType === NAVIGATION_TYPES.nav2023
      const isExternal = book.book?.navigationType === NAVIGATION_TYPES.external

      if (!isNav2023 && !isExternal) {
        this.getResources()
      } else {
        this.getResourcesNav2023()
      }

      if (book.userBook?.status === USER_BOOK_STATUS.expired) {
        return (
          <>
            <Helmet>
              <html lang={this.state.lang} />
            </Helmet>
            <DefaultError
              actionText={i18n.t('Por favor, inténtalo más tarde.')}
              description={i18n.t(
                'Todo apunta a que no tienes acceso a este contenido ya que no está asociado a tu cuenta de Edubook.'
              )}
              title={i18n.t('¡Oops!')}
            />
          </>
        )
      }

      layout = book.book?.type === 'FLIPBOOK_BASIC' ? 3 : layout

      if (isExternal) {
        return this.getExternalLayout()
      }

      if (isNav2023) {
        return this.getNav2023Layout(book)
      }

      return this.getZoomLayout(book)
    }

    this.getResources()

    return (
      <div className={`${index[`Layout${layout}`]} Layout${layout}`}>
        <Helmet>
          <html lang={this.state.lang} />
        </Helmet>
        <Switch>
          <Route
            exact
            path='/viewer/activities/:activityId/attempts/:attemptId'
            render={(props) => <MauthorAttemptContainer isQualifier isRoute {...props} />}
          />
          <Route
            exact
            path='/section/:sectionId/pdf/:pdfId'
            render={(props) => <PdfContainer {...props} />}
          />
          <Route
            exact
            path='/section/:sectionId/video/:videoId'
            render={(props) => <VideoContainer {...props} />}
          />
          <Route
            exact
            path='/section/:sectionId/mauthor/:mauthorId'
            render={(props) => (
              <ErrorBoundary>
                <MauthorContainer {...props} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path='/section/:sectionId/mauthors/:mauthorId'
            render={(props) => (
              <ErrorBoundary>
                <MauthorContainer {...props} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path='/section/:sectionId/simpleactivity/:simpleActivityId'
            render={(props) => (
              <ErrorBoundary>
                <SimpleActivityContainer {...props} />
              </ErrorBoundary>
            )}
          />
          <Route exact path='/' render={(props) => <Redirect {...props} />} />
        </Switch>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    book: state.userBook,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      setCurrentUserBook: userBook,
      setTemplate: template,
    },
    dispatch
  )
}

const TrackedApp = track(
  { app: 'viewer' },
  {
    dispatch: async (data) => {
      if (!window.checkFeatureFlag('ff-track-events')) {
        return
      }
      if (data.action === 'initTracking') {
        sessionStorage.setItem('userId', data.params.userId)
        sessionStorage.setItem('userBookId', data.params.userBookId)
        sessionStorage.setItem('bookId', data.params.bookId)

        const accessType = TRACKING_EVENT_TYPE.accessEvent
        const elementType = TRACKING_ELEMENT_TYPE.book
        const elementId = data.params.bookId

        TrackingCall.postTrack(accessType, elementId, elementType)
      }
      if (TRACKING_ACTIONS_ACCESS_EVENTS.includes(data.action)) {
        if (!data.params.elementId || !data.params.elementType || data.params.elementId === '')
          return
        const accessType = TRACKING_EVENT_TYPE.accessEvent
        TrackingCall.postTrack(accessType, data.params.elementId, data.params.elementType)
      }
    },
  }
)(App)

export default connect(mapStateToProps, mapDispatchToProps)(checkRequest(TrackedApp))
